import { useState } from "react";
import CustomInput from "../atoms/CustomInput";
import CustomSelect from "../atoms/CustomSelect";
import CustomTextArea from "../atoms/CustomTextarea";
import { AlertSuccess, AlertError } from "../atoms/Alerts";
import {
  countries_c4c,
  roles,
  subjects,
} from "../atoms/SelectsInformationJson";
import { SubmitFormButton } from "../atoms/Buttons";
import ConsentCheckbox from "../atoms/ConsentCheckbox";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function BecomeDistributorForm({
  setOpen,
  translations,
  locale,
}) {
  const [name, setName] = useState("");
  const [selectedJob, setSelectedJob] = useState("");
  const [companyname, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  // const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getSorted = (locale, data) => {
    const countries = data[locale];
    return Object.entries(countries)
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([code, name]) => (code));
  };

  const MESSAGES = {
    required: translations.required_field,
    invalidEmail: translations.insert_email,
    submitSuccess: translations.thank_you_become_a_distributor, //"Thank you for your interest in becoming a distributor. We’ll be in touch soon. Thank you.",
  };

  const isValidEmail = (email) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {};

    if (!companyname) errors.companyName = MESSAGES.required;
    if (!name) errors.name = MESSAGES.required;
    if (!isValidEmail(email)) errors.email = MESSAGES.invalidEmail;
    if (!phoneNumber) errors.phoneNumber = MESSAGES.required;
    // if (!selectedSubject) errors.selectedSubject = MESSAGES.required;
    if (!selectedCountry) errors.selectedCountry = MESSAGES.required;
    if (!consent) errors.consent = MESSAGES.required;
    if (!selectedJob) errors.selectedJob = MESSAGES.required;

    return errors;
  };

  const clearForm = () => {
    setCompanyName("");
    setName("");
    setEmail("");
    setPhoneNumber("");
    // setSelectedSubject("");
    setSelectedCountry("");
    setMessage("");
    setConsent(false);
    setSelectedJob("");
    setInputErrors({});
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setInputErrors({});
    setSuccess(false);
    setSubmitting(true);
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setInputErrors(formErrors);
      setSubmitting(false);

    } else {
      try {
        const countryTranslated =
          countries_c4c[locale][selectedCountry] || selectedCountry;
        const jobRoleTranslated = roles[locale][selectedJob] || selectedJob;

        const recaptchaToken = await executeRecaptcha("submit");

        // Prepare the form data
        const distributorContactData = {
          company: companyname,
          name,
          email,
          phone_number: phoneNumber,
          country: selectedCountry,
          // subject: translations?.[selectedSubject] || selectedSubject,
          message,
          job_role: selectedJob,
          country_translated: countryTranslated,
          job_role_translated: jobRoleTranslated,
          language: locale,
          gdpr: consent,
          recaptchaToken
        };

        // Send the form data to Strapi
        const response = await fetch("/api/become-a-distributor-contacts", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ data: distributorContactData }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error details:", errorData);
          setSubmitting(false);
          setInputErrors({
            submit: translations.failed_to_submit,
          });
          return;
        }

        // If the request was successful
        clearForm();
        setSuccess(true);
        setSuccessMessage(MESSAGES.submitSuccess);
        setSubmitting(false);
      } catch (error) {
        console.error("Error submitting distributor contact form:", error);
        setInputErrors({
          submit: translations.failed_to_submit,
        });
        setSubmitting(false);
      }
    }
  };

  return (
    <>
      <AlertSuccess show={success} msg={successMessage} />
      <AlertError show={false} msg="" />
      <form>
        <div className="grid grid-cols-12 gap-16 640:gap-20 1024:gap-24">
          <div className="col-span-12">
            <CustomInput
              label={translations.name}
              name="full-name"
              id="full-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={inputErrors.name}
              bgColor="bg-gray-200"
            />
          </div>

          <div className="col-span-12 640:col-span-6">
            <CustomSelect
              options={getSorted(locale, roles)}
              value={selectedJob}
              onChange={(value) => setSelectedJob(value)}
              placeholder={translations?.job_role}
              required
              error={inputErrors.selectedJob}
              bgColor="bg-gray-200"
              translations={roles[locale]}
            />
          </div>

          <div className="col-span-12 640:col-span-6">
            <CustomInput
              label={translations.company}
              name="company-name"
              id="company-name"
              required
              value={companyname}
              onChange={(e) => setCompanyName(e.target.value)}
              error={inputErrors.companyName}
              bgColor="bg-gray-200"
            />
          </div>

          <div className="col-span-12 640:col-span-6">
            <CustomInput
              label="Email"
              name="email"
              id="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={inputErrors.email}
              bgColor="bg-gray-200"
            />
          </div>

          <div className="col-span-12 640:col-span-6">
            <CustomInput
              label={translations.phone_number}
              name="phone-number"
              id="phone-number"
              required
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              error={inputErrors.phoneNumber}
              bgColor="bg-gray-200"
            />
          </div>

          <div className="col-span-12">
            <CustomSelect
              options={getSorted(locale, countries_c4c)}
              value={selectedCountry}
              onChange={(value) => setSelectedCountry(value)}
              placeholder={translations.country}
              required
              translations={countries_c4c[locale]}
              error={inputErrors.selectedCountry}
              bgColor="bg-gray-200"
            />
          </div>

          {/* <div className="col-span-12">
            <CustomSelect
              options={subjects || []}
              value={selectedSubject}
              onChange={(value) => setSelectedSubject(value)}
              placeholder={translations.subject}
              translations={translations}
              required
              error={inputErrors.subject}
              bgColor="bg-gray-200"
            />
          </div> */}

          <div className="col-span-12">
            <CustomTextArea
              label={translations.message}
              name="message"
              id="message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
              }}
              bgColor="bg-gray-200"
            />
          </div>

          <div className="col-span-12">
            <ConsentCheckbox
              label={translations.confirm_conditions}
              terms_and_conditions={translations.terms_and_conditions}
              privacy_policy_url={translations.privacy_policy_url}
              name="consent"
              id="consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              error={inputErrors.consent}
            />
          </div>

          <div className="col-span-12">
            <SubmitFormButton
              disabled={submitting}
              text={translations.submit_request}
              onClick={submitForm}
            />
            {inputErrors.submit ? <p>{inputErrors.submit}</p> : <></>}
          </div>
        </div>
      </form>
    </>
  );
}
